import { cn } from "@base/lib/utils";
import ButtonGroup from "@base/ui/ButtonGroup";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DayTimeGrid from "./DayTimeGrid";
import { MeetingRoomDatePicker } from "./MeetingRoomDatePicker";
import MeetingRoomNotice from "./MeetingRoomNotice";
import { Button } from "@base/ui/Button/Button";
import { useOverlay } from "@base/model/hooks/useOverlay";
import ReserveRoomDialog from "./ReserveRoomDialog";
import { useQuery } from "@tanstack/react-query";
import { reservationQueries } from "@features/reservation/api/queryFactory";
import dayjs from "dayjs";

const MeetingRoomDashboard = () => {
  const { t } = useTranslation();
  const { open } = useOverlay();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
  const [today, setToday] = useState(() => dayjs());

  useEffect(() => {
    let timeoutId: number;
    const updateToday = () => {
      setToday(dayjs());
      timeoutId = window.setTimeout(updateToday, dayjs().add(1, "day").startOf("day").diff(dayjs()) + 1);
    };

    timeoutId = window.setTimeout(updateToday, dayjs().add(1, "day").startOf("day").diff(dayjs()) + 1);

    return () => window.clearTimeout(timeoutId);
  }, []);

  const isReservationDisabled = !dayjs(selectedDate).isAfter(today, "day");

  const { data, isLoading, isFetching } = useQuery(
    reservationQueries.meetingRooms(dayjs(selectedDate).format("YYYY-MM-DD")),
  );

  const rooms = data?.rooms ?? [];

  const selectedRoom = useMemo(
    () => rooms.find((r) => r.id === selectedRoomId) ?? rooms[0] ?? null,
    [selectedRoomId, rooms],
  );

  const handleReserveRoom = (roomId: number) => {
    if (!dayjs(selectedDate).isAfter(dayjs(), "day")) {
      return;
    }

    const room = rooms.find((r) => r.id === roomId);
    open(({ isOpen, close }) => (
      <ReserveRoomDialog
        isOpen={isOpen}
        onClose={close}
        roomId={roomId}
        roomName={room?.name ?? ""}
        date={selectedDate}
      />
    ));
  };

  return (
    <>
      <div>
        <h2 className="mb-3 text-2xl font-semibold tracking-tighter leading-130 text-gray-1000">
          {t("meetingRoomReservations")}
        </h2>
        <MeetingRoomNotice />
      </div>
      <div className="my-8 md:my-10">
        <MeetingRoomDatePicker value={selectedDate} onValueChange={setSelectedDate} />
      </div>
      {isLoading ? (
        <div className="py-12 text-center text-gray-500 text-label3">Loading...</div>
      ) : rooms.length === 0 ? (
        <div className="py-12 text-center text-gray-500 text-label3">No meeting rooms available</div>
      ) : (
        <div className={cn("transition-opacity duration-200", isFetching && "opacity-50 pointer-events-none")}>
          <div className="pb-24 md:hidden">
            <ButtonGroup
              selectedOption={selectedRoom?.id ?? null}
              onOptionChange={(id) => setSelectedRoomId(id)}
              className="mb-4"
              ariaLabel="Select meeting room"
            >
              {rooms.map((room) => (
                <ButtonGroup.Button key={room.id} value={room.id} label={room.name} />
              ))}
            </ButtonGroup>
            {selectedRoom && <DayTimeGrid reservations={selectedRoom.reservations} />}
          </div>
          <div className="hidden gap-6 md:flex md:flex-row">
            {rooms.map((room) => (
              <div key={room.id} className="flex-1 min-w-0">
                <div className="flex justify-between items-center p-4 mb-2 bg-white border border-gray-300 rounded-8">
                  <h3 className="text-subtitle4 text-gray-1000">{room.name}</h3>
                  <Button disabled={isReservationDisabled} onClick={() => handleReserveRoom(room.id)}>
                    {t("reserveRoom")}
                  </Button>
                </div>
                <DayTimeGrid reservations={room.reservations} />
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedRoom && rooms.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-20 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:hidden">
          <Button
            type="button"
            className="w-full"
            disabled={isReservationDisabled}
            onClick={() => handleReserveRoom(selectedRoom.id)}
          >
            {t("reserveRoom")}
          </Button>
        </div>
      )}
    </>
  );
};

export default MeetingRoomDashboard;
